import { Injectable, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { Subject, catchError, filter, merge, of, switchMap, timer } from 'rxjs';

import VersionData from 'assets/version.json';

import { Announcement, AnnouncementType } from '@shared/models';
import { toDate } from '@shared/utils/time.utils';

import { DismissibleItemStateService } from './dismissible-item-state.service';
import { AnnouncementsService } from './http-client/announcements.service';

@Injectable()
export class AnnouncementDisplayService {
  private readonly router = inject(Router);
  private readonly announcementService = inject(AnnouncementsService);
  private readonly state = inject(DismissibleItemStateService);

  private readonly refreshEvents = new Subject<void>();

  private readonly now = signal(Date.now());
  private readonly currentPath = signal('');
  private readonly announcements = signal<Announcement[]>([]);

  readonly visibleAnnouncements = computed(() =>
    this.announcements().filter(
      (item) =>
        (!item.route || item.route === this.currentPath()) &&
        (!item.to || toDate(item.to).getTime() >= this.now()) &&
        !this.state.isDismissed(`systemMessage.${item.id}.dismissed`)
    )
  );
  readonly messageBars = computed(() =>
    this.visibleAnnouncements().filter((item) => item.type === AnnouncementType.MessageBar)
  );
  readonly chatBubbles = computed(() =>
    this.visibleAnnouncements().filter((item) => item.type === AnnouncementType.ChatBubble)
  );

  constructor() {
    timer(0, 1000)
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.now.set(Date.now()));

    merge(
      timer(0, 60000),
      this.refreshEvents,
      this.router.events.pipe(filter((event) => event instanceof NavigationEnd))
    )
      .pipe(
        switchMap(() => {
          this.currentPath.set(this.router.url.split(/[?#]/)[0].replace(/\/$/, ''));
          return this.announcementService
            .getActive(VersionData.version, this.router.url)
            .pipe(catchError(() => of([])));
        }),
        takeUntilDestroyed()
      )
      .subscribe((announcements) => this.announcements.set(announcements));
  }

  refresh(): void {
    this.refreshEvents.next();
  }
}
